import React, { type ReactNode, useCallback, useEffect, useState } from 'react'

import type { DynamicSizedQuantities } from '@vp/lat-react-component-library'

import type { Quantity, QuantityData } from '../../../../types/Inventory'
import { useWorksDataContext, useWorksDataDispatch } from '../../../root/contexts/WorksDataContext'
import { updateQuantities } from '../../../root/contexts/WorksDataContext/WorkDataAction'
import { useProject } from '../../../root/contexts/ProjectContext'

export interface IQuantityContext {
  initialInputQuantity: Quantity
  inputQuantity: Quantity
  totalQuantity: number
  isValidQuantity: boolean
  updateQuantity: (quantityData: QuantityData, isValid: boolean) => void
}

export const QuantityContext = React.createContext({} as IQuantityContext)

/**
 * Function to remove sizes with invalid quantity.
 *
 * @param {DynamicSizedQuantities} quantities
 * @returns sizes with quantity greater than 0
 */
const cleanQuantities = (quantities: DynamicSizedQuantities) => {
  for (const size in quantities) {
    if (quantities[size] === null || quantities[size] === undefined || quantities[size] <= 0) {
      delete quantities[size]
    }
  }

  return quantities
}

const QuantityContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { work, mpvId, isSizedGood, minimumOrderQuantity, isPrimaryProject } = useProject()
  const worksDataDispatch = useWorksDataDispatch()
  const { worksData } = useWorksDataContext()

  // for hardgood only
  const [initialInputQuantity, setInitialInputQuantity] = useState<Quantity>(0)
  const [inputQuantity, setInputQuantity] = useState<Quantity>({})
  const [totalQuantity, setTotalQuantity] = useState(0)
  const [isValidQuantity, setIsValidQuantity] = useState(false)

  const getQuantityFromWorkOrState = (): Quantity => {
    const localStateWorkData = worksData[work.workId]
    // use the quantity in local state to remain quantity in recovered project
    if (localStateWorkData && localStateWorkData.totalQuantity > 0) {
      return localStateWorkData.inputQuantity
    }
    // use the quantity in work
    if (isSizedGood) {
      return (work.resources['qty'] ? JSON.parse(work.resources['qty']) : {}) as Quantity
    } else {
      return work?.merchandising?.quantity ?? 0
    }
  }

  const getTotalQuantity = (quantity: Quantity): number => {
    let totalQuantity
    if (isSizedGood) {
      totalQuantity = Object.values(quantity as DynamicSizedQuantities).reduce((sum: number, next: number) => {
        return sum + next
      }, 0) as number
    } else {
      totalQuantity = quantity as number
    }

    return totalQuantity
  }

  useEffect(() => {
    if (work.workId && mpvId) {
      const initialQuantity = getQuantityFromWorkOrState()
      setInputQuantity(initialQuantity)
      setInitialInputQuantity(initialQuantity)
      setTotalQuantity(getTotalQuantity(initialQuantity))
    }
  }, [])

  const updateQuantity = useCallback(
    (quantityData: QuantityData, isValid: boolean) => {
      setIsValidQuantity(isValid)
      if (isSizedGood) {
        const quantities = cleanQuantities(quantityData.quantities as DynamicSizedQuantities)
        setInputQuantity(quantities)
        setTotalQuantity((quantityData as QuantityData).total)
        const payload = {
          totalQuantity: (quantityData as QuantityData).total,
          inputQuantity: quantities,
          minimumOrderQuantity: minimumOrderQuantity,
          isValidQuantity: isValid,
        }
        worksDataDispatch(updateQuantities(work.workId, payload, isPrimaryProject))
      } else {
        setInputQuantity(quantityData.quantities as number)
        setTotalQuantity(quantityData.quantities as number)
        const payload = {
          totalQuantity: quantityData.quantities as number,
          inputQuantity: quantityData.quantities as number,
          minimumOrderQuantity: minimumOrderQuantity,
          isValidQuantity: isValid,
        }
        worksDataDispatch(updateQuantities(work.workId, payload, isPrimaryProject))
      }
    },
    [isPrimaryProject, isSizedGood, minimumOrderQuantity, work.workId, worksDataDispatch],
  )

  return (
    <QuantityContext.Provider
      value={{
        initialInputQuantity,
        inputQuantity,
        totalQuantity,
        isValidQuantity,
        updateQuantity,
      }}
    >
      {children}
    </QuantityContext.Provider>
  )
}

export default QuantityContextProvider
